import * as d3 from 'd3';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { DefaultTheme } from 'styled-components';

import { colors } from '@controlrooms/design-tokens';
import { AnomalyRange, Mode, TimeSeries } from '@controlrooms/models';

export enum ChartType {
  LIMIT = 'LIMIT',
  ANOMALY = 'ANOMALY',
}

/**
 * Generate a consistent color for a tag based on its name or index
 * Uses the d3.schemeCategory10 color scheme for consistent coloring across the application
 *
 * Color assignment strategy:
 * 1. If an index is provided, use it directly to pick from the color scheme (preferred)
 * 2. If no index is provided, falls back to hash-based color assignment for backward compatibility
 *
 * This ensures tags maintain consistent colors when displayed in order (e.g. in lists or charts)
 */
export const getTagColor = (tagName: string, index?: number): string => {
  if (!tagName) return d3.schemeCategory10[0];

  // If index is provided, use it directly
  if (typeof index === 'number') {
    return d3.schemeCategory10[index % d3.schemeCategory10.length];
  }

  // Fallback to hash-based color for backward compatibility
  let hash = 0;
  for (let i = 0; i < tagName.length; i++) {
    const char = tagName.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }

  // Use absolute value and modulo to get a valid index
  const index2 = Math.abs(hash) % d3.schemeCategory10.length;
  return d3.schemeCategory10[index2];
};

/**
 * Create a mapping of tag names to colors for multiple tags
 * Uses array indices for consistent color assignment across the application
 *
 * @param tagNames - Array of tag names to create colors for
 * @returns Map of tag names to their assigned colors
 *
 * The color assignment is based on the tag's position in the array,
 * ensuring consistent coloring when tags are displayed in the same order
 */
export const createTagColorMap = (tagNames: string[]): Map<string, string> => {
  const colorMap = new Map<string, string>();
  tagNames.forEach((tagName, index) => {
    colorMap.set(tagName, getTagColor(tagName, index));
  });
  return colorMap;
};

// Hook to observe container size using ResizeObserver
export const useResizeObserver = (ref: React.RefObject<HTMLElement>) => {
  const [dimensions, setDimensions] = useState<{ width: number; height: number } | null>(null);
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const resizeObserver = new ResizeObserver((entries) => {
      if (!entries.length || !entries[0].contentRect) return;
      const { width, height } = entries[0].contentRect;
      setDimensions({ width, height });
    });
    resizeObserver.observe(element);
    return () => {
      resizeObserver.unobserve(element);
    };
  }, [ref]);
  return dimensions;
};

/** Returns the threshold value for a given time from an array of Modes.
 *  If no Mode covers the given time, returns null.
 */
export const getThresholdForTime = (
  thresholds: Mode[] | undefined,
  time: number,
): number | null => {
  if (!thresholds) return null;
  for (const mode of thresholds) {
    if (time >= mode.start && time <= mode.end) {
      return mode.value;
    }
  }
  return null;
};

/** Draw the area chart for one series with limit shadings.
 *  The area (between each point's max and min) is filled in horizontal bands:
 *    - Above highHigh: rgba(255, 113, 113, 0.80)
 *    - Between highHigh and high: rgba(255, 113, 113, 0.30)
 *    - Between high and low: rgba(0, 0, 0, 0.20)
 *    - Between low and lowLow: rgba(255, 113, 113, 0.30)
 *    - Below lowLow: rgba(255, 113, 113, 0.80)
 *
 *  The shading is applied only between the x‑positions of the first and last data points.
 */

// Define an interface for a time segment.
interface TimeSegment {
  start: number;
  end: number;
  effective: {
    highHigh: number | null;
    high: number | null;
    low: number | null;
    lowLow: number | null;
  };
}

/**
 * Given threshold Mode arrays and an overall time range,
 * compute the segments (by time) where threshold settings change.
 * For each segment (defined by adjacent boundaries), determine the effective threshold
 * by taking the Mode value covering the midpoint (if any) for each category.
 */
export const computeThresholdSegments = (
  thresholds: { highHigh: Mode[]; high: Mode[]; low: Mode[]; lowLow: Mode[] },
  overallRange: [number, number],
): TimeSegment[] => {
  const [overallStart, overallEnd] = overallRange;
  // Collect boundaries: overall start and end plus each mode's start and end (if within overall range)
  const boundaries = new Set<number>();
  boundaries.add(overallStart);
  boundaries.add(overallEnd);
  (['highHigh', 'high', 'low', 'lowLow'] as (keyof typeof thresholds)[]).forEach((cat) => {
    thresholds[cat].forEach((mode) => {
      if (mode.start >= overallStart && mode.start <= overallEnd) boundaries.add(mode.start);
      if (mode.end >= overallStart && mode.end <= overallEnd) boundaries.add(mode.end);
    });
  });
  const sortedBoundaries = Array.from(boundaries).sort((a, b) => a - b);
  const segments: TimeSegment[] = [];
  for (let i = 0; i < sortedBoundaries.length - 1; i++) {
    const segStart = sortedBoundaries[i];
    const segEnd = sortedBoundaries[i + 1];
    const mid = (segStart + segEnd) / 2;
    const effective = {
      highHigh: thresholds.highHigh.find((m) => m.start <= mid && m.end >= mid)?.value ?? null,
      high: thresholds.high.find((m) => m.start <= mid && m.end >= mid)?.value ?? null,
      low: thresholds.low.find((m) => m.start <= mid && m.end >= mid)?.value ?? null,
      lowLow: thresholds.lowLow.find((m) => m.start <= mid && m.end >= mid)?.value ?? null,
    };
    segments.push({ start: segStart, end: segEnd, effective });
  }
  return segments;
};

/**
 * Updated drawAreaChart:
 * - thresholds is now of type { highHigh: Mode[]; high: Mode[]; low: Mode[]; lowLow: Mode[] }.
 * - We compute time segments (TS) from the overall timeseries range using computeThresholdSegments.
 * - For each segment, we compute:
 *    xStart = xScale(seg.start), xEnd = xScale(seg.end)
 *    yHighHigh = yScale(effective.highHigh), etc.
 * - Then we fill five horizontal bands for that segment.
 */
export const drawAreaChart = (
  ctx: CanvasRenderingContext2D,
  timeseries: TimeSeries[],
  xScale: d3.ScaleTime<number, number>,
  yScale: d3.ScaleLinear<number, number>,
  thresholds: { highHigh: Mode[]; high: Mode[]; low: Mode[]; lowLow: Mode[] },
  alertType: ChartType,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  theme: any,
) => {
  if (!timeseries.length) return;
  if (!timeseries.every((d) => d.min !== undefined && d.max !== undefined)) return;

  // Build the overall area path (polygon between the max and min values).
  const areaPath = new Path2D();
  areaPath.moveTo(xScale(timeseries[0].time), yScale(timeseries[0].max as number));
  for (let i = 1; i < timeseries.length; i++) {
    areaPath.lineTo(xScale(timeseries[i].time), yScale(timeseries[i].max as number));
  }
  for (let i = timeseries.length - 1; i >= 0; i--) {
    areaPath.lineTo(xScale(timeseries[i].time), yScale(timeseries[i].min as number));
  }
  areaPath.closePath();

  ctx.save();

  // Overall timeseries range.
  const tsStart = timeseries[0].time;
  const tsEnd = timeseries[timeseries.length - 1].time;

  // Compute segments from thresholds.
  const segments = computeThresholdSegments(thresholds, [tsStart, tsEnd]);

  segments.forEach((seg) => {
    // Compute the x-range for the segment.
    const segXStart = xScale(seg.start);
    const segXEnd = xScale(seg.end);
    const segWidth = segXEnd - segXStart;

    // Determine effective thresholds for this segment.
    // For high: prefer a value from thresholds.high if available; otherwise, from thresholds.highHigh.
    const effectiveHigh = seg.effective.high !== null ? seg.effective.high : seg.effective.highHigh;
    // For low: prefer a value from thresholds.low if available; otherwise, from thresholds.lowLow.
    const effectiveLow = seg.effective.low !== null ? seg.effective.low : seg.effective.lowLow;

    // Compute y positions (if effective value is available).
    const yEffHigh = effectiveHigh !== null ? yScale(effectiveHigh) : null;
    const yEffLow = effectiveLow !== null ? yScale(effectiveLow) : null;

    // Now, fill the area in bands:
    if (yEffHigh !== null && yEffLow !== null) {
      // Both thresholds exist:
      // Exceedance above effectiveHigh.
      ctx.save();
      ctx.beginPath();
      ctx.rect(segXStart, 0, segWidth, yEffHigh);
      ctx.clip();
      ctx.fillStyle =
        alertType === ChartType.ANOMALY ? theme.chart.areaFillColor : colors.datavis['pink-1-50'];
      ctx.fill(areaPath);
      ctx.restore();

      // Safe region between effectiveHigh and effectiveLow.
      ctx.save();
      ctx.beginPath();
      ctx.rect(segXStart, yEffHigh, segWidth, yEffLow - yEffHigh);
      ctx.clip();
      ctx.fillStyle = theme.chart.areaFillColor;
      ctx.fill(areaPath);
      ctx.restore();

      // Exceedance below effectiveLow.
      ctx.save();
      ctx.beginPath();
      ctx.rect(segXStart, yEffLow, segWidth, ctx.canvas.height);
      ctx.clip();
      ctx.fillStyle =
        alertType === ChartType.ANOMALY ? theme.chart.areaFillColor : colors.datavis['pink-1-50'];
      ctx.fill(areaPath);
      ctx.restore();
    } else if (yEffHigh !== null && yEffLow === null) {
      // Only effective high exists.
      // Safe region is below effective high; exceedance region is above.
      // Exceedance (above effectiveHigh)
      ctx.save();
      ctx.beginPath();
      ctx.rect(segXStart, 0, segWidth, yEffHigh);
      ctx.clip();
      ctx.fillStyle =
        alertType === ChartType.ANOMALY ? theme.chart.areaFillColor : colors.datavis['pink-1-50'];
      ctx.fill(areaPath);
      ctx.restore();
      // Safe (below effectiveHigh)
      ctx.save();
      ctx.beginPath();
      ctx.rect(segXStart, yEffHigh, segWidth, ctx.canvas.height);
      ctx.clip();
      ctx.fillStyle = theme.chart.areaFillColor;
      ctx.fill(areaPath);
      ctx.restore();
    } else if (yEffHigh === null && yEffLow !== null) {
      // Only effective low exists.
      // Safe region is above effective low; exceedance region is below.
      // Safe (above effectiveLow)
      ctx.save();
      ctx.beginPath();
      ctx.rect(segXStart, 0, segWidth, yEffLow);
      ctx.clip();
      ctx.fillStyle = theme.chart.areaFillColor;
      ctx.fill(areaPath);
      ctx.restore();
      // Exceedance (below effectiveLow)
      ctx.save();
      ctx.beginPath();
      ctx.rect(segXStart, yEffLow, segWidth, ctx.canvas.height);
      ctx.clip();
      ctx.fillStyle =
        alertType === ChartType.ANOMALY ? theme.chart.areaFillColor : colors.datavis['pink-1-50'];
      ctx.fill(areaPath);
      ctx.restore();
    }
  });

  ctx.restore();
};

export const drawMAEAreaChartV2 = (
  ctx: CanvasRenderingContext2D,
  timeseries: TimeSeries[],
  xScale: d3.ScaleTime<number, number>,
  yScale: d3.ScaleLinear<number, number>,
  theme: DefaultTheme,
) => {
  if (!timeseries.length) return;
  if (!timeseries.every((d) => d.min !== undefined && d.max !== undefined)) return;

  // Create area generator
  const area = d3
    .area<TimeSeries>()
    .x((d) => xScale(d.time))
    .y0((d) => yScale(d.min as number))
    .y1((d) => yScale(d.max as number))
    .curve(d3.curveLinear)
    .context(ctx);

  // Draw the area
  ctx.beginPath();
  area(timeseries);
  ctx.fillStyle = theme.modelVisualizer.predictedAreaBG;
  ctx.fill();
  // ctx.strokeStyle = theme.modelVisualizer.predictedAreaBorder;
  // ctx.lineWidth = 1;
  // ctx.stroke();
};

/** Draw the area chart with 5-zone limit shading. */
export const drawAreaChartV2 = (
  ctx: CanvasRenderingContext2D,
  timeseries: TimeSeries[],
  xScale: d3.ScaleTime<number, number>,
  yScale: d3.ScaleLinear<number, number>,
  thresholds: { highHigh: Mode[]; high: Mode[]; low: Mode[]; lowLow: Mode[] },
  alertType: ChartType,
  theme: DefaultTheme,
) => {
  if (!timeseries.length) return;
  if (!timeseries.every((d) => d.min !== undefined && d.max !== undefined)) return;

  // Build the overall area path (polygon between the max and min values).
  const areaPath = new Path2D();
  areaPath.moveTo(xScale(timeseries[0].time), yScale(timeseries[0].max as number));
  for (let i = 1; i < timeseries.length; i++) {
    areaPath.lineTo(xScale(timeseries[i].time), yScale(timeseries[i].max as number));
  }
  for (let i = timeseries.length - 1; i >= 0; i--) {
    areaPath.lineTo(xScale(timeseries[i].time), yScale(timeseries[i].min as number));
  }
  areaPath.closePath();

  // 2) Determine overall time span
  const tsStart = timeseries[0].time;
  const tsEnd = timeseries[timeseries.length - 1].time;

  // 3) Break into segments where thresholds change
  const segments = computeThresholdSegments(thresholds, [tsStart as number, tsEnd as number]);

  // 4) Define your five zone colors
  const colorHH = theme.modelVisualizer.criticalArea2BG;
  const colorH = theme.modelVisualizer.criticalArea1BG;
  const colorS = theme.modelVisualizer.areaBG;
  const colorL = theme.modelVisualizer.criticalArea1BG;
  const colorLL = theme.modelVisualizer.criticalArea2BG;

  ctx.save();
  for (const seg of segments) {
    // clip to this segment's horizontal span
    const x0 = xScale(seg.start);
    const x1 = xScale(seg.end);
    const width = x1 - x0;

    // determine effective threshold values at the segment midpoint
    const hh = seg.effective.highHigh ?? Infinity;
    const h = seg.effective.high ?? hh;
    const l = seg.effective.low ?? -Infinity;
    const ll = seg.effective.lowLow ?? l;

    // map thresholds to y-positions
    const yHH = isFinite(hh) ? yScale(hh) : null;
    const yH = isFinite(h) ? yScale(h) : null;
    const yL = isFinite(l) ? yScale(l) : null;
    const yLL = isFinite(ll) ? yScale(ll) : null;

    // helper to fill a horizontal band
    const fillBand = (top: number, height: number, col: string) => {
      ctx.save();
      ctx.beginPath();
      ctx.rect(x0, top, width, height);
      ctx.clip();
      ctx.fillStyle = col;
      ctx.fill(areaPath);
      ctx.restore();
    };

    if (yHH === null && yH === null && yL === null && yLL === null) {
      // No limits defined - fill entire area with safe color
      const fillBand = (top: number, height: number, col: string) => {
        ctx.save();
        ctx.beginPath();
        ctx.rect(x0, top, width, height);
        ctx.clip();
        ctx.fillStyle = col;
        ctx.fill(areaPath);
        ctx.restore();
      };
      fillBand(0, ctx.canvas.height, theme.chart.areaFillColor);
    }

    // 5 zones, only fill if positions exist
    //  • Above highHigh
    if (yHH !== null) {
      fillBand(0, yHH, colorHH);
    }
    //  • Between highHigh→high
    if (yHH !== null && yH !== null) {
      fillBand(yHH, yH - yHH, colorH);
    }
    //  • Safe zone high→low
    if (yH !== null && yL !== null) {
      fillBand(yH, yL - yH, colorS);
    }
    //  • Between low→lowLow
    if (yL !== null && yLL !== null) {
      fillBand(yL, yLL - yL, colorL);
    }
    //  • Below lowLow
    if (yLL !== null) {
      fillBand(yLL, ctx.canvas.height, colorLL);
    }
  }
  ctx.restore();
};

/** Draw the normal data line for one series. */
export const drawDataLine = (
  ctx: CanvasRenderingContext2D,
  timeseries: { time: Date | number; value: number }[],
  xScale: d3.ScaleTime<number, number>,
  yScale: d3.ScaleLinear<number, number>,
  color: string,
) => {
  ctx.beginPath();
  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  timeseries.forEach((d, i) => {
    const x = xScale(d.time);
    const y = yScale(d.value);
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  });
  ctx.stroke();
};

/** Draw the predicted data line for one series with a dotted line style. */
export const drawPredictedLine = (
  ctx: CanvasRenderingContext2D,
  timeseries: { time: Date | number; value: number }[],
  xScale: d3.ScaleTime<number, number>,
  yScale: d3.ScaleLinear<number, number>,
  color = '#D9D9D9',
) => {
  ctx.beginPath();
  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  ctx.setLineDash([5, 2]); // Set dotted line style
  timeseries.forEach((d, i) => {
    const x = xScale(d.time);
    const y = yScale(d.value);
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  });
  ctx.stroke();
  ctx.setLineDash([]); // Reset line style
};

/** Draw coordinate axes. */
export const drawAxes = (
  ctx: CanvasRenderingContext2D,
  innerWidth: number,
  innerHeight: number,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  theme: any,
) => {
  ctx.beginPath();
  ctx.strokeStyle = theme.timeLine.minorTickColor; // Y-axis
  ctx.lineWidth = 1;
  ctx.moveTo(0.5, 0);
  ctx.lineTo(0.5, innerHeight);
  ctx.stroke();
  ctx.beginPath();
  ctx.strokeStyle = 'transparent'; // X-axis
  ctx.lineWidth = 1;
  ctx.moveTo(0, innerHeight + 0.5);
  ctx.lineTo(innerWidth, innerHeight + 0.5);
  ctx.stroke();
};

/** Draw x-axis ticks and labels. */
export const drawXAxisTicks = (
  ctx: CanvasRenderingContext2D,
  xScale: d3.ScaleTime<number, number>,
  innerWidth: number,
  innerHeight: number,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  theme: any,
  timezone?: string,
) => {
  const majorTicks = xScale.ticks(5);
  const allTicks = xScale.ticks(20);
  const minorTicks = allTicks.filter(
    (t) => !majorTicks.some((mt) => Math.abs(t.getTime() - mt.getTime()) < 1000),
  );

  // Draw minor ticks
  ctx.strokeStyle = theme.timeLine.minorTickColor;
  minorTicks.forEach((tick) => {
    const x = xScale(tick);
    ctx.beginPath();
    ctx.moveTo(x + 0.5, innerHeight + 0.5);
    ctx.lineTo(x + 0.5, innerHeight + 3);
    ctx.stroke();
  });

  // Draw major ticks
  ctx.strokeStyle = theme.timeLine.majorTickColor;
  majorTicks.forEach((tick) => {
    const x = xScale(tick);
    ctx.beginPath();
    ctx.moveTo(x + 0.5, innerHeight + 0.5);
    ctx.lineTo(x + 0.5, innerHeight + 5);
    ctx.stroke();
  });

  // Draw labels for first and last tick from all ticks
  ctx.textBaseline = 'top';
  ctx.fillStyle = theme.timeLine.majorTickColor;
  ctx.font = '12px Arial';

  if (allTicks.length > 0) {
    // First tick label
    const firstTick = allTicks[0];
    const firstX = xScale(firstTick);
    const firstLabel = timezone
      ? dayjs.utc(firstTick).tz(timezone).format('MMM DD, YYYY HH:mm')
      : d3.timeFormat('%b %d, %Y %H:%M')(firstTick);
    ctx.textAlign = 'left';
    ctx.fillText(firstLabel, Math.max(0, firstX - 5), innerHeight + 7);

    // Last tick label (only if there's more than one tick)
    if (allTicks.length > 1) {
      const lastTick = allTicks[allTicks.length - 1];
      const lastX = xScale(lastTick);
      const lastLabel = timezone
        ? dayjs.utc(lastTick).tz(timezone).format('MMM DD, YYYY HH:mm')
        : d3.timeFormat('%b %d, %Y %H:%M')(lastTick);
      ctx.textAlign = 'right';
      ctx.fillText(lastLabel, Math.min(innerWidth, lastX + 5), innerHeight + 7);
    }
  }

  // Reset text alignment
  ctx.textAlign = 'left';
};

/** Draw x-axis ticks and labels. */
export const drawXAxisTicksV2 = (
  ctx: CanvasRenderingContext2D,
  xScale: d3.ScaleTime<number, number>,
  innerWidth: number,
  innerHeight: number,
  theme: DefaultTheme,
  tickInterval: d3.TimeInterval | null,
  timezone: string,
) => {
  const majorTicks = tickInterval ? xScale.ticks(tickInterval) : xScale.ticks(5);

  const allTicks = xScale.ticks(20);
  const minorTicks = allTicks.filter(
    (t) => !majorTicks.some((mt) => Math.abs(t.getTime() - mt.getTime()) < 1e3),
  );

  // …then draw them as you already do
  ctx.strokeStyle = theme.timeLine.minorTickColor;
  minorTicks.forEach((tick) => {
    const x = xScale(tick);
    ctx.beginPath();
    ctx.moveTo(x + 0.5, innerHeight + 0.5);
    ctx.lineTo(x + 0.5, innerHeight + 3);
    ctx.stroke();
  });
  ctx.textBaseline = 'top';
  ctx.fillStyle = theme.modelVisualizer.textPrimary;
  ctx.font = '9px Open Sans';
  majorTicks.forEach((tick) => {
    const x = xScale(tick);
    ctx.beginPath();
    ctx.strokeStyle = theme.timeLine.majorTickColor;
    ctx.moveTo(x + 0.5, innerHeight + 0.5);
    ctx.lineTo(x + 0.5, innerHeight + 5);
    ctx.stroke();

    const timeStr = dayjs(tick).tz(timezone).format('HH:mm');
    if (timeStr === '00:00') {
      // Draw time
      ctx.textAlign = 'center';
      // ctx.fillText('00:00', x, innerHeight + 7);
      // Draw date below
      ctx.font = '9px Open Sans';
      ctx.fillText(d3.timeFormat('%b %d')(tick), x, innerHeight + 7);
      ctx.font = '9px Open Sans'; // reset font if needed
    } else {
      ctx.textAlign = 'center';
      ctx.fillText(timeStr, x, innerHeight + 7);
    }
  });
};

/** Draw y-axis ticks with values. */
export const drawYAxisTicks = (
  ctx: CanvasRenderingContext2D,
  yScale: d3.ScaleLinear<number, number>,
) => {
  const yTicks = yScale.ticks(5);
  ctx.textAlign = 'right';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = '#000';
  ctx.font = '12px Arial';
  yTicks.forEach((tick) => {
    const y = yScale(tick);
    ctx.beginPath();
    ctx.strokeStyle = '';
    ctx.moveTo(-5 + 0.5, y + 0.5);
    ctx.lineTo(0 + 0.5, y + 0.5);
    ctx.stroke();
    ctx.fillText(String(tick), -7, y);
  });
};

/** Draw y-axis ticks with values. */
export const drawYAxisTicksV2 = (
  ctx: CanvasRenderingContext2D,
  yScale: d3.ScaleLinear<number, number>,
  tickCount = 5,
  theme: DefaultTheme,
) => {
  const yTicks = yScale.ticks(tickCount);
  ctx.textAlign = 'right';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = theme.modelVisualizer.textPrimary;
  ctx.font = '9px Open Sans';
  yTicks.forEach((tick) => {
    const y = yScale(tick);
    ctx.beginPath();
    ctx.strokeStyle = '';
    ctx.moveTo(-5 + 0.5, y + 0.5);
    ctx.lineTo(0 + 0.5, y + 0.5);
    ctx.stroke();
    ctx.fillText(String(tick), -7, y);
  });
};

/** Draw anomaly lines for one series. */
export const drawAnomalyLines = (
  ctx: CanvasRenderingContext2D,
  anomalyData: AnomalyRange[] | undefined,
  xScale: d3.ScaleTime<number, number>,
  yScale: d3.ScaleLinear<number, number>,
  tagColor: string,
  exportImg = false,
) => {
  if (!anomalyData || anomalyData.length === 0) return;
  ctx.save();
  ctx.filter = exportImg ? 'none' : 'blur(2px)';
  ctx.lineWidth = 5;
  ctx.strokeStyle = tagColor;
  anomalyData.forEach((anomaly) => {
    if (anomaly.yScale && anomaly.yScale.length > 0) {
      ctx.beginPath();
      anomaly.yScale.forEach((point, i) => {
        const x = xScale(dayjs(point.time).valueOf());
        const y = yScale(point.value);
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });
      ctx.stroke();
    }
  });
  ctx.restore();
};

/** Draw anomaly lines for one series. */
export const drawAnomalyLinesV2 = (
  ctx: CanvasRenderingContext2D,
  anomalyData: AnomalyRange[] | undefined,
  xScale: d3.ScaleTime<number, number>,
  yScale: d3.ScaleLinear<number, number>,
  tagColor: string,
  exportImg = false,
) => {
  if (!anomalyData || anomalyData.length === 0) return;
  ctx.save();
  ctx.filter = exportImg ? 'none' : 'blur(2px)';
  ctx.lineWidth = 5;
  ctx.strokeStyle = tagColor;
  anomalyData.forEach((anomaly) => {
    if (anomaly.yScale && anomaly.yScale.length > 0) {
      ctx.beginPath();
      anomaly.yScale.forEach((point, i) => {
        const x = xScale(point.time); // point.time is now pre-converted to valueOf()
        const y = yScale(point.value);
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });
      ctx.stroke();
    }
  });
  ctx.restore();
};

/** Draw horizontal limit lines with labels over specified time ranges. */
export const drawLimitLines = (
  ctx: CanvasRenderingContext2D,
  limits: { highHighs?: Mode[]; highs?: Mode[]; lowLows?: Mode[]; lows?: Mode[] },
  xScale: d3.ScaleTime<number, number>,
  yScale: d3.ScaleLinear<number, number>,
  innerWidth: number,
  theme: DefaultTheme,
) => {
  const drawLineForLimits = (limitArray: Mode[] | undefined, label: string, color: string) => {
    if (!limitArray) return;
    limitArray.forEach((mode) => {
      const xStart = Math.max(0, xScale(mode.start));
      const xEnd = Math.min(innerWidth, xScale(mode.end));
      if (xEnd <= xStart) return;
      const y = yScale(mode.value);
      ctx.beginPath();
      ctx.strokeStyle = color;
      ctx.setLineDash([
        label === 'HighHigh' || label === 'LowLow' ? 2 : 5,
        label === 'HighHigh' || label === 'LowLow' ? 5 : 10,
      ]);
      ctx.lineWidth = 1;
      ctx.moveTo(xStart, y + 0.5);
      ctx.lineTo(xEnd, y + 0.5);
      ctx.stroke();
      ctx.fillStyle = color;
      ctx.font = '10px Arial';
      ctx.textBaseline = 'middle';
      // ctx.fillText(label, xStart + 2, y);
    });
  };
  drawLineForLimits(limits.highHighs, 'HighHigh', theme.modelVisualizer.criticalArea2BG);
  drawLineForLimits(limits.highs, 'High', theme.modelVisualizer.criticalArea2BG);
  drawLineForLimits(limits.lowLows, 'LowLow', theme.modelVisualizer.criticalArea2BG);
  drawLineForLimits(limits.lows, 'Low', theme.modelVisualizer.criticalArea2BG);
};

/** Draw crosshair and hovered point. */
export const drawCrosshair = (
  ctx: CanvasRenderingContext2D,
  hoveredPoint: { x: number; y: number } | null,
  innerWidth: number,
  innerHeight: number,
  theme: DefaultTheme,
) => {
  if (!hoveredPoint) return;
  ctx.save();
  ctx.setLineDash([5, 5]);
  ctx.strokeStyle = theme.modelVisualizer.crosshairLine;
  ctx.beginPath();
  ctx.moveTo(hoveredPoint.x + 0.5, 0);
  ctx.lineTo(hoveredPoint.x + 0.5, innerHeight);
  ctx.stroke();
  // ctx.beginPath();
  // ctx.moveTo(0, hoveredPoint.y + 0.5);
  // ctx.lineTo(innerWidth, hoveredPoint.y + 0.5);
  // ctx.stroke();
  ctx.restore();
  ctx.beginPath();
  ctx.arc(hoveredPoint.x, hoveredPoint.y, 5, 0, 2 * Math.PI);
  ctx.fillStyle = theme.modelVisualizer.dataCircle;
  ctx.fill();
};

/** Draw shaded data limit line segments.
 *  Segments in the safe zone (between low and high) are drawn in black.
 *  Segments above high or below low are drawn in '#FF7171'.
 */
export const drawDataLimitLine = (
  ctx: CanvasRenderingContext2D,
  timeseries: TimeSeries[],
  xScale: d3.ScaleTime<number, number>,
  yScale: d3.ScaleLinear<number, number>,
  thresholds: { high: Mode[]; highHigh: Mode[]; low: Mode[]; lowLow: Mode[] },
  tagColor: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  theme: any,
) => {
  if (!timeseries || timeseries.length < 2) return;

  const safeColor = tagColor;
  const exceedanceColor = theme.chart.limitIndicator;

  // Helper: compute intersection point along the segment where value equals threshold.
  const computeIntersection = (pt1: TimeSeries, pt2: TimeSeries, threshold: number): number =>
    pt1.time + ((threshold - pt1.value) * (pt2.time - pt1.time)) / (pt2.value - pt1.value);

  // Helper: draw a line segment.
  const drawSegment = (
    startTime: number,
    startValue: number,
    endTime: number,
    endValue: number,
    color: string,
  ) => {
    ctx.beginPath();
    ctx.strokeStyle = color;
    ctx.moveTo(xScale(startTime), yScale(startValue));
    ctx.lineTo(xScale(endTime), yScale(endValue));
    ctx.stroke();
  };

  for (let i = 0; i < timeseries.length - 1; i++) {
    const pt1 = timeseries[i];
    const pt2 = timeseries[i + 1];
    const midTime = (pt1.time + pt2.time) / 2;

    // Compute effective high threshold:
    const highCandidate = thresholds.high ? getThresholdForTime(thresholds.high, midTime) : null;
    const highHighCandidate = thresholds.highHigh
      ? getThresholdForTime(thresholds.highHigh, midTime)
      : null;
    const effectiveHigh = highCandidate !== null ? highCandidate : highHighCandidate;
    // Compute effective low threshold:
    const lowCandidate = thresholds.low ? getThresholdForTime(thresholds.low, midTime) : null;
    const lowLowCandidate = thresholds.lowLow
      ? getThresholdForTime(thresholds.lowLow, midTime)
      : null;
    const effectiveLow = lowCandidate !== null ? lowCandidate : lowLowCandidate;

    // If no effective thresholds available, treat segment as safe.
    if (effectiveHigh === null && effectiveLow === null) {
      drawSegment(pt1.time, pt1.value, pt2.time, pt2.value, safeColor);
      continue;
    }
    const currentHigh = effectiveHigh !== null ? effectiveHigh : Infinity;
    const currentLow = effectiveLow !== null ? effectiveLow : -Infinity;

    // Determine status for each point.
    const status1 =
      pt1.value > currentHigh
        ? 'HIGH_EXCEEDANCE'
        : pt1.value < currentLow
        ? 'LOW_EXCEEDANCE'
        : 'safe';
    const status2 =
      pt2.value > currentHigh
        ? 'HIGH_EXCEEDANCE'
        : pt2.value < currentLow
        ? 'LOW_EXCEEDANCE'
        : 'safe';

    if (status1 === status2) {
      const color = status1 === 'safe' ? safeColor : exceedanceColor;
      drawSegment(pt1.time, pt1.value, pt2.time, pt2.value, color);
    } else {
      const segments: {
        startTime: number;
        startValue: number;
        endTime: number;
        endValue: number;
        color: string;
      }[] = [];
      if (status1 === 'safe' && status2 === 'HIGH_EXCEEDANCE') {
        const intersectTime = computeIntersection(pt1, pt2, currentHigh);
        segments.push({
          startTime: pt1.time,
          startValue: pt1.value,
          endTime: intersectTime,
          endValue: currentHigh,
          color: safeColor,
        });
        segments.push({
          startTime: intersectTime,
          startValue: currentHigh,
          endTime: pt2.time,
          endValue: pt2.value,
          color: exceedanceColor,
        });
      } else if (status1 === 'HIGH_EXCEEDANCE' && status2 === 'safe') {
        const intersectTime = computeIntersection(pt1, pt2, currentHigh);
        segments.push({
          startTime: pt1.time,
          startValue: pt1.value,
          endTime: intersectTime,
          endValue: currentHigh,
          color: exceedanceColor,
        });
        segments.push({
          startTime: intersectTime,
          startValue: currentHigh,
          endTime: pt2.time,
          endValue: pt2.value,
          color: safeColor,
        });
      } else if (status1 === 'safe' && status2 === 'LOW_EXCEEDANCE') {
        const intersectTime = computeIntersection(pt1, pt2, currentLow);
        segments.push({
          startTime: pt1.time,
          startValue: pt1.value,
          endTime: intersectTime,
          endValue: currentLow,
          color: safeColor,
        });
        segments.push({
          startTime: intersectTime,
          startValue: currentLow,
          endTime: pt2.time,
          endValue: pt2.value,
          color: exceedanceColor,
        });
      } else if (status1 === 'LOW_EXCEEDANCE' && status2 === 'safe') {
        const intersectTime = computeIntersection(pt1, pt2, currentLow);
        segments.push({
          startTime: pt1.time,
          startValue: pt1.value,
          endTime: intersectTime,
          endValue: currentLow,
          color: exceedanceColor,
        });
        segments.push({
          startTime: intersectTime,
          startValue: currentLow,
          endTime: pt2.time,
          endValue: pt2.value,
          color: safeColor,
        });
      } else if (status1 === 'HIGH_EXCEEDANCE' && status2 === 'LOW_EXCEEDANCE') {
        // Crosses from above to below: segment crosses both thresholds.
        const tHigh = computeIntersection(pt1, pt2, currentHigh);
        const tLow = computeIntersection(pt1, pt2, currentLow);
        segments.push({
          startTime: pt1.time,
          startValue: pt1.value,
          endTime: tHigh,
          endValue: currentHigh,
          color: exceedanceColor,
        });
        segments.push({
          startTime: tHigh,
          startValue: currentHigh,
          endTime: tLow,
          endValue: currentLow,
          color: safeColor,
        });
        segments.push({
          startTime: tLow,
          startValue: currentLow,
          endTime: pt2.time,
          endValue: pt2.value,
          color: exceedanceColor,
        });
      } else if (status1 === 'LOW_EXCEEDANCE' && status2 === 'HIGH_EXCEEDANCE') {
        const tLow = computeIntersection(pt1, pt2, currentLow);
        const tHigh = computeIntersection(pt1, pt2, currentHigh);
        segments.push({
          startTime: pt1.time,
          startValue: pt1.value,
          endTime: tLow,
          endValue: currentLow,
          color: exceedanceColor,
        });
        segments.push({
          startTime: tLow,
          startValue: currentLow,
          endTime: tHigh,
          endValue: currentHigh,
          color: safeColor,
        });
        segments.push({
          startTime: tHigh,
          startValue: currentHigh,
          endTime: pt2.time,
          endValue: pt2.value,
          color: exceedanceColor,
        });
      }
      segments.forEach((seg) => {
        drawSegment(seg.startTime, seg.startValue, seg.endTime, seg.endValue, seg.color);
      });
    }
  }
};

/** Draw shaded data limit line segments with 5 distinct regions:
 *  • value ≥ highHigh      → colorHighHigh
 *  • high ≤ value < highHigh → colorHigh
 *  • low ≤ value < high      → colorSafe
 *  • lowLow ≤ value < low    → colorLow
 *  • value < lowLow         → colorLowLow
 */
export const drawDataLimitLineV2 = (
  ctx: CanvasRenderingContext2D,
  timeseries: TimeSeries[],
  xScale: d3.ScaleTime<number, number>,
  yScale: d3.ScaleLinear<number, number>,
  thresholds: { highHigh: Mode[]; high: Mode[]; low: Mode[]; lowLow: Mode[] },
  theme: DefaultTheme,
  isPredicted = false,
) => {
  if (!timeseries || timeseries.length < 2) return;

  // your colors
  const colorHighHigh = theme.modelVisualizer.dataLineCritical2;
  const colorHigh = theme.modelVisualizer.dataLineCritical1;
  const colorSafe = theme.modelVisualizer.dataLine;
  const colorLow = theme.modelVisualizer.dataLineCritical1;
  const colorLowLow = theme.modelVisualizer.dataLineCritical2;

  // intersection helper
  const computeIntersection = (p1: TimeSeries, p2: TimeSeries, t: number) =>
    p1.time + ((t - p1.value) * (p2.time - p1.time)) / (p2.value - p1.value);

  // for a given time, find the applicable threshold value (or null)
  const getVal = (arr: Mode[], time: number) => {
    const m = arr.find((m) => time >= m.start && time <= m.end);
    return m ? m.value : null;
  };

  // determine region for a value
  const getRegion = (v: number, hh: number, h: number, l: number, ll: number): string => {
    if (v >= hh) return 'aboveHighHigh';
    if (v >= h) return 'betweenHighHighHigh';
    if (v >= l) return 'safe';
    if (v >= ll) return 'betweenLowLowLow';
    return 'belowLowLow';
  };

  // draw a single segment
  const drawSeg = (t0: number, v0: number, t1: number, v1: number, col: string) => {
    ctx.beginPath();
    ctx.strokeStyle = col;
    if (isPredicted) {
      ctx.setLineDash([5, 5]);
    }
    ctx.moveTo(xScale(t0), yScale(v0));
    ctx.lineTo(xScale(t1), yScale(v1));
    ctx.stroke();
  };

  for (let i = 0; i < timeseries.length - 1; i++) {
    const p1 = timeseries[i];
    const p2 = timeseries[i + 1];
    // compute mid‐time to pick thresholds
    const mid = (p1.time + p2.time) / 2;

    // get the four threshold values at this mid‐time (or null)
    const hhVal = getVal(thresholds.highHigh, mid) ?? Infinity;
    const hVal = getVal(thresholds.high, mid) ?? hhVal;
    const lVal = getVal(thresholds.low, mid) ?? -Infinity;
    const llVal = getVal(thresholds.lowLow, mid) ?? lVal;

    // collect all crossing times
    const crossings: { time: number; value: number }[] = [];
    [hhVal, hVal, lVal, llVal].forEach((th) => {
      if (isFinite(th)) {
        // only compute if p1 and p2 straddle the threshold
        if ((p1.value - th) * (p2.value - th) < 0) {
          crossings.push({ time: computeIntersection(p1, p2, th), value: th });
        }
      }
    });

    // sort by time
    crossings.sort((a, b) => a.time - b.time);

    // build full list of points for this segment
    const pts = [
      { time: p1.time, value: p1.value },
      ...crossings,
      { time: p2.time, value: p2.value },
    ];

    // now draw each sub‐segment
    for (let j = 0; j < pts.length - 1; j++) {
      const a = pts[j];
      const b = pts[j + 1];
      // pick midpoint to determine the region
      const mt = (a.time + b.time) / 2;
      const mv = a.value + ((b.value - a.value) * (mt - a.time)) / (b.time - a.time);
      const region = getRegion(mv, hhVal, hVal, lVal, llVal);

      let col: string;
      switch (region) {
        case 'aboveHighHigh':
          col = colorHighHigh;
          break;
        case 'betweenHighHighHigh':
          col = colorHigh;
          break;
        case 'safe':
          col = colorSafe;
          break;
        case 'betweenLowLowLow':
          col = colorLow;
          break;
        case 'belowLowLow':
          col = colorLowLow;
          break;
        default:
          col = colorSafe;
      }
      drawSeg(a.time, a.value, b.time, b.value, col);
    }
  }
};
